"use client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { ApiError, api } from "./api";
import { useAppStore } from "./store";
import type { Cart, Product, User } from "./types";
import { calculateGuestCart } from "./cart";

let cartMergeInFlight = false;
let favoriteMergeInFlight = false;

export function useAuth() {
  return useQuery({
    queryKey: ["auth"],
    queryFn: async () => { try { return (await api<{ user: User }>("/auth/me")).user; } catch (error) { if (error instanceof ApiError && error.status === 401) return null; throw error; } },
    retry: false,
  });
}

export function useCart() {
  const auth = useAuth();
  const locale = useAppStore((state) => state.locale);
  const guestItems = useAppStore((state) => state.cart);
  const setGuestQuantity = useAppStore((state) => state.setQuantity);
  const clearGuestCart = useAppStore((state) => state.clearCart);
  const queryClient = useQueryClient();
  const serverCart = useQuery({ queryKey: ["cart", locale], queryFn: () => api<Cart>(`/cart?locale=${locale}`), enabled: Boolean(auth.data), retry: false });
  const guestCart = useMemo<Cart>(() => calculateGuestCart(guestItems), [guestItems]);
  const mutation = useMutation({
    mutationFn: ({ product, quantity }: { product: Product; quantity: number }) => api<Cart>(`/cart/items?locale=${locale}`, { method: "POST", body: JSON.stringify({ productId: product.id, quantity }) }),
    onSuccess: (data) => queryClient.setQueryData(["cart", locale], data),
  });
  useEffect(() => {
    if (!auth.data || !guestItems.length || cartMergeInFlight) return;
    cartMergeInFlight = true;
    void api<Cart>(`/cart/merge?locale=${locale}`, { method: "POST", body: JSON.stringify({ items: guestItems.map((item) => ({ productId: item.product.id, quantity: item.quantity })) }) })
      .then((data) => { queryClient.setQueryData(["cart", locale], data); clearGuestCart(); })
      .catch(() => undefined)
      .finally(() => { cartMergeInFlight = false; });
  }, [auth.data, clearGuestCart, guestItems, locale, queryClient]);
  const setQuantity = (product: Product, quantity: number) => auth.data ? mutation.mutateAsync({ product, quantity }) : Promise.resolve(setGuestQuantity(product, quantity));
  const quantityFor = (productId: string) => (auth.data ? serverCart.data?.items : guestCart.items)?.find((item) => item.product.id === productId)?.quantity ?? 0;
  return { cart: auth.data ? serverCart.data : guestCart, isLoading: auth.isLoading || (Boolean(auth.data) && serverCart.isLoading), isAuthenticated: Boolean(auth.data), setQuantity, quantityFor, isUpdating: mutation.isPending, refetch: serverCart.refetch };
}

export function useFavorites() {
  const auth = useAuth();
  const locale = useAppStore((state) => state.locale);
  const local = useAppStore((state) => state.favorites);
  const toggleLocal = useAppStore((state) => state.toggleFavorite);
  const setLocal = useAppStore((state) => state.setFavorites);
  const queryClient = useQueryClient();
  const server = useQuery({ queryKey: ["favorites", locale], queryFn: () => api<Product[]>(`/favorites?locale=${locale}`), enabled: Boolean(auth.data), retry: false });
  const products = auth.data ? server.data ?? [] : local;
  useEffect(() => {
    if (!auth.data || !local.length || favoriteMergeInFlight) return;
    favoriteMergeInFlight = true;
    void Promise.all(local.map((item) => api(`/favorites/${item.id}`, { method: "POST" })))
      .then(() => { setLocal([]); return queryClient.invalidateQueries({ queryKey: ["favorites"] }); })
      .catch(() => undefined)
      .finally(() => { favoriteMergeInFlight = false; });
  }, [auth.data, local, queryClient, setLocal]);
  const toggle = async (product: Product) => {
    if (!auth.data) { toggleLocal(product); return; }
    const exists = products.some((item) => item.id === product.id);
    await api(`/favorites/${product.id}`, { method: exists ? "DELETE" : "POST" });
    await queryClient.invalidateQueries({ queryKey: ["favorites"] });
  };
  return { products, toggle, has: (id: string) => products.some((item) => item.id === id), isLoading: server.isLoading && Boolean(auth.data) };
}
